// src/services/venueApi.ts
import axios, { type AxiosResponse } from 'axios';
import authService from './authService';
import { getApiBaseUrl } from './apiConfig';

export interface VenueRecord {
  id: string;
  tenantId?: string | null;
  managedEntityId?: string | null;
  parentEntityId?: string | null;
  userId: string;
  name: string;
  description?: string | null;
  isPublic?: boolean;
  socialLinks?: Record<string, any> | any[] | null;
  createdAt?: string;
  updatedAt?: string;
  isLinked?: boolean;
  /** Admin-approved blue-check (verificationStatus === 'verified'). Distinct
   *  from isLinked/managedEntityId — a real linked venue can be unverified. */
  isVerified?: boolean;
  isFollowing?: boolean;
  profilePicture?: string | null;
  customLinks?: Array<Record<string, any>> | null;
  /** Off-platform merch store (issue #43), validated http(s) by the server. */
  merchUrl?: string | null;
  defaults?: Record<string, any> | null;
  upcomingEvents?: any[];
  connectionStatus?: 'pending' | 'accepted' | 'rejected';
  email?: string; // Added for invitations
}

const API_BASE = `${getApiBaseUrl()}/api/venues`;

const withAuth = () => ({ headers: authService.getAuthHeader() });

export const venueApi = {
  async list(params: { tenantId?: string; managedEntityId?: string; term?: string; limit?: number; offset?: number; scope?: 'global' | 'network' | 'local' } = {}): Promise<VenueRecord[]> {
    const res: AxiosResponse<VenueRecord[]> = await axios.get(API_BASE, {
      params,
      ...withAuth(),
    });
    return res.data;
  },



  async getById(id: string): Promise<VenueRecord> {
    const res: AxiosResponse<VenueRecord> = await axios.get(`${API_BASE}/${id}`, withAuth());
    return res.data;
  },

  async create(input: { name: string; description?: string; socialLinks?: Record<string, any>; managedEntityId?: string; parentEntityId?: string }): Promise<VenueRecord> {
    const res: AxiosResponse<VenueRecord> = await axios.post(API_BASE, input, withAuth());
    return res.data;
  },

  async update(id: string, patch: Partial<VenueRecord>): Promise<VenueRecord> {
    const res: AxiosResponse<VenueRecord> = await axios.put(`${API_BASE}/${id}`, patch, withAuth());
    return res.data;
  },

  async delete(id: string): Promise<void> {
    await axios.delete(`${API_BASE}/${id}`, withAuth());
  },

  /**
   * Get the venue owned BY the calling user's workspace. Pass the active
   * workspace's id so the backend can resolve which venue belongs to it
   * (a single user can own multiple venue workspaces).
   */
  async getMyVenue(managedEntityId: string): Promise<VenueRecord> {
    const res: AxiosResponse<VenueRecord> = await axios.get(`${API_BASE}/my-venue`, {
      params: { managedEntityId },
      ...withAuth(),
    });
    return res.data;
  },

  async getVenue(id: string): Promise<VenueRecord> {
    const res: AxiosResponse<VenueRecord> = await axios.get(`${API_BASE}/${id}`);
    return res.data;
  },
};

export default venueApi;
